/**
 * The outbox: what is happening to messages that have been sent but have not
 * left.
 *
 * # Why a strip in the corner and not a mailbox in the sidebar
 *
 * The obvious place is a folder beside Drafts, and it is the wrong one, for
 * three reasons that all point the same way.
 *
 * *It is empty almost always.* A sidebar row reading "Outbox 0" nine hundred
 * times teaches the eye to skip it, and the one morning it says 1 the eye skips
 * it then too. Something that appears only when it has something to say is read
 * every time it appears.
 *
 * *Undo has a deadline measured in seconds.* A destination you have to navigate
 * to is not reachable inside a ten-second window -- by the time the folder is
 * open the message has gone. The undo affordance has to already be on screen at
 * the moment it becomes available, which means it cannot be somewhere you go.
 *
 * *It has to be visible from wherever you were.* The composer closes the
 * instant the daemon accepts the message, and where the user goes next is not
 * predictable: back to the list, to Settings, to another account's inbox. A
 * strip pinned to the viewport is on all of them; a folder in the mail view is
 * on one.
 *
 * So: bottom-right, over everything, out of the way of the two things already
 * pinned to the bottom of this app -- the centred toasts and the install hint
 * at bottom-left. It is not dismissible as a whole, only row by row, and the
 * rows that matter have no timer on them at all.
 *
 * # The one row that must never disappear on its own
 *
 * A `parked` submission is a message whose SMTP dialogue was interrupted and
 * which the daemon deliberately will not retry, because a duplicate in the
 * recipient's mailbox cannot be undone. It is the only state in which mail
 * silently does not arrive: nothing is in Sent, nothing failed loudly, and the
 * sender has no reason to suspect anything. Its row stays until somebody
 * dismisses it by hand, and it is the only state here that also raises a toast.
 *
 * # Undo is offered only while it is guaranteed
 *
 * The button appears while the message is `held` and vanishes the moment
 * `sendAt` passes, because that is exactly where `outbox::claim` draws the line
 * on the server. It can still lose the race -- the drain may claim the row
 * between the paint and the click -- and when it does, the row says it was too
 * late rather than pretending to have worked.
 */

import { useEffect } from "preact/hooks";

import * as composeWindow from "../lib/composeWindow";
import * as outbox from "../lib/outbox";
import * as store from "../lib/store";
import * as Icon from "./Icons";

export function Outbox() {
  const items = outbox.items.value;

  // Pick up anything a previous session left behind -- the parked rows, mostly
  // -- and re-ask whenever the set of accounts changes, since an account added
  // or connected mid-session brings its own outbox with it.
  const accountKey = store.accounts.value.map((a) => a.id).join(",");
  useEffect(() => {
    if (accountKey) void outbox.adopt();
  }, [accountKey]);

  // A tab that was in the background for an hour has been shown nothing, and
  // the poller stops once everything it was watching settled. Coming back is
  // the moment to find out what happened while nobody was looking.
  useEffect(() => {
    const onShown = () => {
      if (document.visibilityState === "visible") void outbox.adopt();
    };
    document.addEventListener("visibilitychange", onShown);
    return () => document.removeEventListener("visibilitychange", onShown);
  }, []);

  if (items.length === 0) return null;

  return (
    <section class="outbox" aria-label="Outbox">
      <h2 class="outbox-head">
        <Icon.Send />
        <span>Outbox</span>
        <span class="outbox-count">{items.length}</span>
      </h2>
      {/*
        Polite rather than assertive. Each row announces itself as it changes,
        which is the running commentary somebody who cannot see the strip needs;
        interrupting them mid-sentence to say a message was queued would not be.
      */}
      <ul aria-live="polite">
        {items.map((item) => (
          <Row key={item.key} item={item} />
        ))}
      </ul>
    </section>
  );
}

function Row({ item }: { item: outbox.Item }) {
  const remaining = secondsLeft(item, outbox.tick.value);

  return (
    <li class={`outbox-row ${item.stage}`}>
      <span class="outbox-mark">{mark(item.stage)}</span>
      <span class="outbox-what">
        <strong class="outbox-subject">{item.subject}</strong>
        <span class="outbox-to">{recipientLine(item.recipients)}</span>
        <span class="outbox-state">{stateLine(item, remaining)}</span>
        {/* Only ever set for something the reader has to act on, so it is not
            a second line of chatter under every row. */}
        {item.detail && <span class="outbox-detail">{item.detail}</span>}
      </span>
      <span class="outbox-actions">
        {/* Gone the instant the countdown reaches zero, not on the next poll.
            The button is a promise that pressing it works, and the last second
            of a window is exactly when somebody lunges for it. */}
        {item.stage === "held" && remaining !== null && remaining > 0 && (
          <button class="btn" onClick={() => void withdraw(item)}>
            Undo
          </button>
        )}
        {item.stage === "cancelled" && item.emailId && (
          <button class="btn" onClick={() => void reopen(item)}>
            Edit
          </button>
        )}
        {/* Nothing to dismiss while it is still happening: the row is not a
            notification about the message, it *is* the message's progress, and
            closing it would be the only way to lose track of a send. */}
        {(item.stage === "parked" || item.stage === "cancelled" || item.stage === "sending") && (
          <button
            class="icon-btn"
            aria-label={`Dismiss ${item.subject}`}
            onClick={() => outbox.dismiss(item.key)}
          >
            <Icon.Close />
          </button>
        )}
      </span>
    </li>
  );
}

/** Whole seconds until this message may leave, or null when that is not a question. */
function secondsLeft(item: outbox.Item, now: number): number | null {
  if (item.stage !== "held" || item.sendAt === null) return null;
  return Math.max(0, Math.ceil((item.sendAt - now) / 1000));
}

function mark(stage: outbox.Stage) {
  switch (stage) {
    case "held":
    case "sending":
      return <span class="spinner" />;
    case "sent":
      return <Icon.Check />;
    case "parked":
      return <Icon.Warning />;
    case "cancelled":
      return <Icon.Pencil />;
  }
}

/**
 * What is happening, in the fewest words that are still true.
 *
 * "Sending" for a released message rather than "Sent": the daemon has been
 * allowed to start and this client cannot see whether it has. Claiming the
 * stronger word a second early is how a client ends up having said "Sent" about
 * a message that parked.
 */
function stateLine(item: outbox.Item, remaining: number | null): string {
  switch (item.stage) {
    case "held":
      return remaining === null
        ? "Waiting to send"
        : remaining > 0
          ? `Sending in ${remaining}s — Undo while you can`
          : "Sending now";
    case "sending":
      return "Sending…";
    case "sent":
      return "Sent";
    case "parked":
      return "Not confirmed as sent";
    case "cancelled":
      return "Not sent. The message is back in Drafts.";
  }
}

/**
 * Who it is to, at a glance.
 *
 * Named in full up to two, then counted. The point of the line is recognising
 * *which* message this row is, and past two addresses a list stops helping with
 * that and starts wrapping instead.
 */
function recipientLine(to: string[]): string {
  if (to.length === 0) return "";
  if (to.length <= 2) return `To ${to.join(", ")}`;
  return `To ${to[0]} and ${to.length - 1} others`;
}

/**
 * Take it back, and say plainly which of the two things happened.
 *
 * The success path reopens the message rather than leaving it withdrawn and
 * filed: somebody who presses Undo within ten seconds of Send is going to
 * change something, and the alternative is a client that responds to "stop" by
 * putting the message somewhere and not saying where.
 */
async function withdraw(item: outbox.Item): Promise<void> {
  const outcome = await outbox.undo(item.key);
  if (outcome === "done") await reopen(item);
  else if (outcome === "tooLate") {
    store.toast("Too late — that message had already gone to the mail server.", "error");
  }
}

/**
 * Put a withdrawn message back in the composer.
 *
 * The draft never moved: `onSuccessUpdateEmail` files it into Sent only after a
 * send actually succeeds, so a cancelled message is still sitting in Drafts
 * with `$draft` on it, and reopening it resumes the same upstream revision
 * rather than forking a second copy.
 */
async function reopen(item: outbox.Item): Promise<void> {
  if (!item.emailId) return;
  try {
    // Through `resumeDraft` rather than `compose.openDraft`, so a withdrawn
    // message comes back in a window on the desktop like every other composer.
    // It also stops fetching the message here: on the desktop the new window is
    // the one that needs it, and this process cannot hand it across anyway.
    await composeWindow.resumeDraft({ account: item.accountId, email: item.emailId });
    outbox.dismiss(item.key);
  } catch {
    // The row stays, and so does its Edit button. Saying where the message is
    // matters more than the failure to fetch it: nothing has been lost, and
    // Drafts is where to look.
    store.toast("That message is in Drafts — it could not be reopened here.", "error");
  }
}
